import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDomainCalculation } from '@web_opxp/features';
import { EDomain } from '../base/types';
import { Types as ExercisesTypes } from '../exercises';
import {
  IDomainProgress,
  IDomainsProgress,
  IHighscoreResponse,
  ILoadEvaluationHighscoresPayload,
  ILoadEvaluationProgressHistoryPayload,
  ILoadEvaluationsHighscoresForSingleExercisePayload,
  IUpdateEvaluationProgressPayload,
} from './types';

export interface IEvaluationState extends IDomainsProgress {
  rawData: IDomainCalculation[];
  evaluationReady: boolean;
  evaluation: {
    [domainId: number]: IDomainProgress[];
  };
  highscoresReady: boolean;
  highscores: IHighscoreResponse[];
  singleExerciseHighscoresReady: boolean;
  singleExerciseHighscores: IHighscoreResponse[];
  exercises?: {
    [domainId: number]: ExercisesTypes.IExercise[];
  };
}

export const initialState: IEvaluationState = {
  rawData: [],
  evaluationReady: false,
  evaluation: {
    [EDomain.Speed]: [],
    [EDomain.Reasoning]: [],
    [EDomain.Attention]: [],
    [EDomain.Memory]: [],
    [EDomain.Forgetfulness]: [],
    [EDomain.Distractibility]: [],
    [EDomain.FalseTriggering]: [],
  },
  highscoresReady: false,
  highscores: [],
  singleExerciseHighscoresReady: false,
  singleExerciseHighscores: [],
};

export const statePropName = 'evaluation';

const evaluationSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    openEvaluation(state, action: PayloadAction<number | undefined>) {},
    initEvaluation(state) {},
    evaluationReady(state) {
      state.evaluationReady = true;
    },
    updateEvaluationProgress: {
      reducer(state, action: PayloadAction<IUpdateEvaluationProgressPayload>) {
        const { progress, domain } = action.payload;
        state.evaluation[domain] = progress;
      },
      prepare(progress: IDomainProgress[], domain: EDomain) {
        return { payload: { progress, domain } };
      },
    },
    getCycleEvaluation(state, action: PayloadAction<number>) {},
    cycleEvaluationResult(state, action: PayloadAction<IDomainCalculation[]>) {
      state.rawData.push(...action.payload);
    },
    loadEvaluationHighscores: {
      reducer(state, action: PayloadAction<ILoadEvaluationHighscoresPayload>) {},
      prepare(workoutIds: number[], userId?: number) {
        return { payload: { workoutIds, userId } };
      },
    },
    loadEvaluationsHighscoresForSingleExercise: {
      reducer(state, action: PayloadAction<ILoadEvaluationsHighscoresForSingleExercisePayload>) {
        state.singleExerciseHighscoresReady = false;
      },
      prepare(workoutIds: number[], userId?: number) {
        return { payload: { workoutIds, userId } };
      },
    },
    updateEvaluationHighscores(state, action: PayloadAction<IHighscoreResponse[]>) {
      state.highscores = action.payload;
      state.highscoresReady = true;
    },
    updateEvaluationSingleExerciseHighscores(state, action: PayloadAction<IHighscoreResponse[]>) {
      state.singleExerciseHighscores = action.payload;
      state.singleExerciseHighscoresReady = true;
    },
    loadProgress(state, action: PayloadAction<number | undefined>) {},
    loadProgressHistory: {
      reducer(state, action: PayloadAction<ILoadEvaluationProgressHistoryPayload>) {},
      prepare(startTimestamp: number, endTimestamp: number, userId?: number) {
        return { payload: { startTimestamp, endTimestamp, userId } };
      },
    },
    initPerformancePage(state, action: PayloadAction<number | undefined>) {},
  },
});

export const EvaluationActions = evaluationSlice.actions;
export default evaluationSlice.reducer;
